import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CustomerReport {
  id: string;
  report_type: 'product_discovery' | 'lead_generation';
  week_start: string;
  published_at: string | null;
}

interface Funnel {
  sends: number;
  accepts: number;
  accept_rate?: number | null;
  responses: number;
  response_rate?: number | null;
}

interface Quote {
  text: string;
  lead_name: string | null;
  lead_linkedin_url: string | null;
  bucket: 'positive' | 'neutral' | 'negative';
  why_notable: string;
}

interface Objection {
  text: string;
  count: number;
  implication?: string;
  reframe?: string;
}

interface NextStep {
  action: string;
  priority: 'urgent' | 'high' | 'medium';
}

interface HotLead {
  lead_name: string | null;
  lead_linkedin_url: string | null;
  intent_label: string;
  key_quote: string;
  suggested_action: string;
}

interface WinLoss {
  positive: number;
  neutral: number;
  negative: number;
  top_positive_label: string;
  top_negative_label: string;
}

interface MsgPerf {
  breakdown: Record<string, number>;
  best_message: number | null;
  insight: string;
}

interface IcpBrief {
  target_persona: string;
  success_criteria: string;
  failure_mode: string;
}

interface ConnectingThread {
  question_answered: string;
  answer_summary: string;
  next_question: string;
  next_campaign: string;
}

interface CampaignSetup {
  target_audience: string;
  campaign_type: string;
  objective: string;
  success_threshold: string;
}

interface SplitTestVariant {
  campaign_name: string;
  sends: number;
  accepts: number;
  accept_rate_pct: number;
  responses: number;
  response_rate_pct: number;
  positive_count: number;
  best_message: number | null;
}

interface SplitTest {
  variants: SplitTestVariant[];
  winner: string;
  winning_dimension: string;
  insight: string;
}

interface TitleBreakdownItem {
  title: string;
  count: number;
  bucket: 'positive' | 'neutral' | 'negative';
  example_outcome: string;
}

interface CostPerOutcome {
  sends_per_accept: number | null;
  sends_per_response: number | null;
  sends_per_positive: number | null;
  note: string;
}

interface ReportContent {
  id: string;
  group_id: string;
  verdict: string | null;
  verdict_status: string | null;
  hypothesis_short: string | null;
  icp_brief: IcpBrief | null;
  sequence_note: string | null;
  connecting_thread: ConnectingThread | null;
  sample_note: string | null;
  quotes: Quote[] | null;
  objections: Objection[] | null;
  hot_leads: HotLead[] | null;
  win_loss_summary: WinLoss | null;
  message_performance: MsgPerf | null;
  performance_tier: string | null;
  campaign_setup: CampaignSetup | null;
  split_test: SplitTest | null;
  title_breakdown: TitleBreakdownItem[] | null;
  cost_per_outcome: CostPerOutcome | null;
  recommendation: string | null;
  next_steps: NextStep[] | null;
  date_range_start: string | null;
  date_range_end: string | null;
}

interface Group {
  id: string;
  group_name: string;
  funnel: Funnel | null;
  classified_breakdown: Record<string, number> | null;
  content: ReportContent | null;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function FunnelStats({ funnel, sampleNote }: { funnel: Funnel; sampleNote: string | null }) {
  const acceptRate = funnel.accept_rate ??
    (funnel.sends > 0 ? Math.round((funnel.accepts / funnel.sends) * 100) : 0);
  const responseRate =
    funnel.accepts > 0 ? Math.round((funnel.responses / funnel.accepts) * 100) : 0;

  const items = [
    { label: 'Sends', value: funnel.sends, sub: null, color: 'bg-gray-400' },
    { label: 'Accepts', value: funnel.accepts, sub: `${acceptRate}%`, color: 'bg-blue-400' },
    { label: 'Responses', value: funnel.responses, sub: `${responseRate}% of accepts`, color: 'bg-purple-500' },
  ];

  return (
    <div className="mb-5">
      <div className="flex items-center gap-3">
        {items.map((item, i) => (
          <React.Fragment key={item.label}>
            <div className="text-center">
              <div
                className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${item.color} text-white font-bold text-lg`}
              >
                {item.value}
              </div>
              <div className="text-xs font-medium text-gray-500 mt-1">{item.label}</div>
              {item.sub && <div className="text-xs text-gray-400">{item.sub}</div>}
            </div>
            {i < items.length - 1 && (
              <span className="text-gray-300 text-xl font-light mt-[-12px]">→</span>
            )}
          </React.Fragment>
        ))}
      </div>
      {sampleNote && (
        <p className="text-xs text-gray-400 mt-3 italic">{sampleNote}</p>
      )}
    </div>
  );
}

function VerdictBox({ verdict }: { verdict: string }) {
  return (
    <div className="bg-purple-50 border border-purple-200 rounded-xl px-5 py-4 mb-6">
      <div className="text-xs font-semibold text-purple-500 uppercase tracking-wider mb-1">
        Key Finding
      </div>
      <p className="text-gray-900 font-semibold text-base leading-snug">{verdict}</p>
    </div>
  );
}

const PRIORITY_STYLE: Record<string, string> = {
  urgent: 'bg-red-100 text-red-700',
  high:   'bg-amber-100 text-amber-700',
  medium: 'bg-blue-100 text-blue-600',
};

function NextStepsList({ steps }: { steps: NextStep[] }) {
  return (
    <div className="space-y-2">
      {steps.map((s, i) => (
        <div key={i} className="flex items-start gap-3">
          <span
            className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5 ${PRIORITY_STYLE[s.priority]}`}
          >
            {s.priority}
          </span>
          <p className="text-sm text-gray-700">{s.action}</p>
        </div>
      ))}
    </div>
  );
}

const BUCKET_STYLE: Record<string, string> = {
  positive: 'border-l-green-400 bg-green-50',
  neutral:  'border-l-gray-300 bg-gray-50',
  negative: 'border-l-red-300 bg-red-50',
};

function QuoteList({ quotes }: { quotes: Quote[] }) {
  return (
    <div className="space-y-3">
      {quotes.map((q, i) => (
        <div key={i} className={`border-l-4 rounded-r-lg px-4 py-3 ${BUCKET_STYLE[q.bucket] ?? BUCKET_STYLE.neutral}`}>
          <p className="text-sm text-gray-800 italic">"{q.text}"</p>
          {q.lead_name && (
            q.lead_linkedin_url ? (
              <a
                href={q.lead_linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-purple-600 hover:underline mt-1 inline-block"
              >
                — {q.lead_name}
              </a>
            ) : (
              <p className="text-xs text-gray-400 mt-1">— {q.lead_name}</p>
            )
          )}
          {q.why_notable && (
            <p className="text-xs text-gray-500 mt-1.5">{q.why_notable}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
        {title}
      </h3>
      {children}
    </div>
  );
}

const VERDICT_STATUS_STYLE: Record<string, string> = {
  'CONFIRMED':    'bg-green-100 text-green-800 border-green-300',
  'DENIED':       'bg-red-100 text-red-800 border-red-300',
  'EARLY SIGNAL': 'bg-blue-100 text-blue-800 border-blue-300',
  'MIXED':        'bg-amber-100 text-amber-800 border-amber-300',
  'INCONCLUSIVE': 'bg-gray-100 text-gray-600 border-gray-300',
};

const TIER_STYLE: Record<string, string> = {
  'STRONG':   'bg-green-100 text-green-800 border-green-300',
  'MODERATE': 'bg-blue-100 text-blue-800 border-blue-300',
  'EARLY':    'bg-amber-100 text-amber-800 border-amber-300',
  'WATCH':    'bg-red-100 text-red-800 border-red-300',
};

const TIER_BORDER: Record<string, string> = {
  'STRONG':   'border-l-green-500',
  'MODERATE': 'border-l-blue-500',
  'EARLY':    'border-l-amber-500',
  'WATCH':    'border-l-red-500',
};

function VerdictStatusBadge({ status }: { status: string }) {
  return (
    <div className="mb-3">
      <span className={`inline-block text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border ${VERDICT_STATUS_STYLE[status] ?? 'bg-gray-100 text-gray-600 border-gray-300'}`}>
        {status}
      </span>
    </div>
  );
}

function PerformanceTierBadge({ tier }: { tier: string }) {
  return (
    <div className="mb-3">
      <span className={`inline-block text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border ${TIER_STYLE[tier] ?? 'bg-gray-100 text-gray-600 border-gray-300'}`}>
        {tier}
      </span>
    </div>
  );
}

function KeyValueTable({ rows }: { rows: Array<{ label: string; value: string | null }> }) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {rows.map((row, i) => (
        <div key={i} className={`flex ${i > 0 ? 'border-t border-gray-200' : ''}`}>
          <div className="w-36 shrink-0 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-500 border-r border-gray-200">
            {row.label}
          </div>
          <div className="px-3 py-2 text-xs text-gray-700 leading-relaxed">
            {row.value ?? '—'}
          </div>
        </div>
      ))}
    </div>
  );
}

const BUCKET_PILL: Record<string, string> = {
  positive: 'bg-green-100 text-green-700',
  neutral:  'bg-gray-100 text-gray-600',
  negative: 'bg-red-100 text-red-600',
};

function SplitTestComparison({ st }: { st: SplitTest }) {
  const winner = st.winner;
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: `repeat(${st.variants.length}, 1fr)` }}>
        {st.variants.map((v, i) => {
          const isWinner = v.campaign_name === winner;
          return (
            <div
              key={i}
              className={`px-4 py-4 ${i > 0 ? 'border-l border-gray-200' : ''} ${isWinner ? 'bg-green-50' : 'bg-white'}`}
            >
              <div className="flex items-center gap-1.5 mb-3">
                <span className="text-xs font-semibold text-gray-700 truncate">{v.campaign_name}</span>
                {isWinner && (
                  <span className="shrink-0 text-xs font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded-full">Winner</span>
                )}
              </div>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Sends</span>
                  <span className="font-medium text-gray-800">{v.sends}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Accept rate</span>
                  <span className={`font-medium ${isWinner ? 'text-green-700' : 'text-gray-800'}`}>{v.accept_rate_pct}%</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Responses</span>
                  <span className="font-medium text-gray-800">{v.responses}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-gray-100 pt-1.5 mt-1.5">
                  <span className="text-gray-500">Response rate</span>
                  <span className={`font-bold ${isWinner ? 'text-green-700' : 'text-gray-800'}`}>{v.response_rate_pct}%</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Positive</span>
                  <span className="font-medium text-green-700">{v.positive_count}</span>
                </div>
                {v.best_message != null && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Best message</span>
                    <span className="font-medium text-purple-700">Msg {v.best_message}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {st.insight && (
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-2.5">
          <p className="text-xs text-gray-600 italic">{st.insight}</p>
        </div>
      )}
    </div>
  );
}

function TitleBreakdownSection({ items }: { items: TitleBreakdownItem[] }) {
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${BUCKET_PILL[item.bucket] ?? BUCKET_PILL.neutral}`}>
            {item.bucket}
          </span>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-800">{item.title}</span>
            {item.count > 1 && <span className="text-xs text-gray-400 ml-1">×{item.count}</span>}
          </div>
          <span className="text-xs text-gray-500 shrink-0">{item.example_outcome}</span>
        </div>
      ))}
    </div>
  );
}

function CostPerOutcomeSection({ cpo }: { cpo: CostPerOutcome }) {
  const stats = [
    { label: 'Sends per accept',   value: cpo.sends_per_accept },
    { label: 'Sends per response', value: cpo.sends_per_response },
    { label: 'Sends per positive', value: cpo.sends_per_positive },
  ].filter(s => s.value != null);

  if (!stats.length) return null;

  return (
    <div>
      <div className="flex gap-4 mb-2">
        {stats.map((s, i) => (
          <div key={i} className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-center">
            <div className="text-xl font-bold text-gray-800">{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>
      {cpo.note && <p className="text-xs text-gray-400 italic">{cpo.note}</p>}
    </div>
  );
}

// ─── Product Discovery content ─────────────────────────────────────────────────

function ProductDiscoveryContent({ c }: { c: ReportContent }) {
  return (
    <>
      {c.verdict_status && <VerdictStatusBadge status={c.verdict_status} />}

      {c.hypothesis_short && (
        <Section title="Hypothesis Signal">
          <p className="text-sm text-gray-700 leading-relaxed">{c.hypothesis_short}</p>
        </Section>
      )}

      {c.icp_brief && (
        <Section title="Campaign Brief">
          <KeyValueTable rows={[
            { label: 'Target Persona',   value: c.icp_brief.target_persona },
            { label: 'Success Criteria', value: c.icp_brief.success_criteria },
            { label: 'Failure Mode',     value: c.icp_brief.failure_mode },
          ]} />
        </Section>
      )}

      {c.sequence_note && (
        <Section title="Sequence Analysis">
          <p className="text-sm text-gray-700 leading-relaxed">{c.sequence_note}</p>
        </Section>
      )}

      {c.quotes && c.quotes.length > 0 && (
        <Section title="Notable Responses">
          <QuoteList quotes={c.quotes} />
        </Section>
      )}

      {c.objections && c.objections.length > 0 && (
        <Section title="Friction Themes">
          <div className="space-y-3">
            {c.objections.map((o, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-gray-800">{o.text}</p>
                  {o.count > 1 && (
                    <span className="shrink-0 text-xs text-gray-400">×{o.count}</span>
                  )}
                </div>
                {o.implication && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    <span className="font-medium">Implication:</span> {o.implication}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {c.recommendation && (
        <Section title="Recommendation">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
            <p className="text-sm text-gray-800 leading-relaxed">{c.recommendation}</p>
          </div>
        </Section>
      )}

      {c.next_steps && c.next_steps.length > 0 && (
        <Section title="Next Steps">
          <NextStepsList steps={c.next_steps} />
        </Section>
      )}

      {c.connecting_thread && c.connecting_thread.question_answered && (
        <Section title="Research Thread">
          <KeyValueTable rows={[
            { label: 'Question Answered', value: c.connecting_thread.question_answered },
            { label: 'Answer Summary',    value: c.connecting_thread.answer_summary },
            { label: 'Next Question',     value: c.connecting_thread.next_question },
            { label: 'Next Campaign',     value: c.connecting_thread.next_campaign },
          ]} />
        </Section>
      )}
    </>
  );
}

// ─── Lead Generation content ───────────────────────────────────────────────────

function LeadGenerationContent({ c }: { c: ReportContent }) {
  const wl = c.win_loss_summary;

  return (
    <>
      {c.performance_tier && <PerformanceTierBadge tier={c.performance_tier} />}

      {c.verdict && (
        <div className={`border-l-4 rounded-r-xl bg-green-50 border border-green-200 px-5 py-4 mb-6 ${TIER_BORDER[c.performance_tier ?? ''] ?? 'border-l-green-400'}`}>
          <div className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">
            Pipeline Summary
          </div>
          <p className="text-gray-900 font-semibold text-base leading-snug">{c.verdict}</p>
        </div>
      )}

      {c.campaign_setup && (
        <Section title="Campaign Setup">
          <KeyValueTable rows={[
            { label: 'Target Audience',   value: c.campaign_setup.target_audience },
            { label: 'Campaign Type',     value: c.campaign_setup.campaign_type },
            { label: 'Objective',         value: c.campaign_setup.objective },
            { label: 'Success Threshold', value: c.campaign_setup.success_threshold },
          ]} />
        </Section>
      )}

      {c.split_test && c.split_test.variants.length > 1 && (
        <Section title="Split Test Comparison">
          <SplitTestComparison st={c.split_test} />
        </Section>
      )}

      {c.hot_leads && c.hot_leads.length > 0 && (
        <Section title="Hot Leads — Follow Up Now">
          <div className="space-y-3">
            {c.hot_leads.map((lead, i) => (
              <div key={i} className="bg-green-50 border border-green-200 rounded-xl px-5 py-4">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <div>
                    {lead.lead_name && (
                      lead.lead_linkedin_url ? (
                        <a
                          href={lead.lead_linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-semibold text-gray-900 hover:text-purple-700 hover:underline text-sm"
                        >
                          {lead.lead_name} ↗
                        </a>
                      ) : (
                        <span className="font-semibold text-gray-900 text-sm">{lead.lead_name}</span>
                      )
                    )}
                    <span className="ml-2 text-xs text-green-700 font-medium">{lead.intent_label}</span>
                  </div>
                </div>
                {lead.key_quote && (
                  <p className="text-sm text-gray-700 italic mb-2">"{lead.key_quote}"</p>
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-500">Suggested action:</span>
                  <span className="text-xs text-gray-700">{lead.suggested_action}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {c.title_breakdown && c.title_breakdown.length > 0 && (
        <Section title="Who's Responding">
          <TitleBreakdownSection items={c.title_breakdown} />
        </Section>
      )}

      {wl && (
        <Section title="Win / Loss Picture">
          <div className="flex gap-4 mb-3">
            <div className="flex-1 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-center">
              <div className="text-2xl font-bold text-green-700">{wl.positive}</div>
              <div className="text-xs text-gray-500 mt-1">Positive</div>
              {wl.top_positive_label && (
                <div className="text-xs text-green-600 font-medium mt-0.5">{wl.top_positive_label}</div>
              )}
            </div>
            <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-center">
              <div className="text-2xl font-bold text-gray-500">{wl.neutral}</div>
              <div className="text-xs text-gray-500 mt-1">Neutral</div>
            </div>
            <div className="flex-1 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-center">
              <div className="text-2xl font-bold text-red-600">{wl.negative}</div>
              <div className="text-xs text-gray-500 mt-1">Negative</div>
              {wl.top_negative_label && (
                <div className="text-xs text-red-500 font-medium mt-0.5">{wl.top_negative_label}</div>
              )}
            </div>
          </div>
        </Section>
      )}

      {c.message_performance && (
        <Section title="Message Performance">
          <div className="bg-white border border-gray-200 rounded-xl px-5 py-4">
            {Object.keys(c.message_performance.breakdown ?? {}).length > 0 && (
              <div className="flex gap-4 mb-3">
                {Object.entries(c.message_performance.breakdown).map(([msgNum, count]) => (
                  <div
                    key={msgNum}
                    className={`flex-1 text-center rounded-lg py-2 border ${
                      c.message_performance!.best_message === Number(msgNum)
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className={`text-xl font-bold ${
                      c.message_performance!.best_message === Number(msgNum)
                        ? 'text-purple-700'
                        : 'text-gray-500'
                    }`}>
                      {count}
                    </div>
                    <div className="text-xs text-gray-400">
                      Msg {msgNum}
                      {c.message_performance!.best_message === Number(msgNum) && (
                        <span className="text-purple-500 ml-1">★</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {c.message_performance.insight && (
              <p className="text-sm text-gray-600">{c.message_performance.insight}</p>
            )}
          </div>
        </Section>
      )}

      {c.cost_per_outcome && (
        <Section title="Cost Per Outcome">
          <CostPerOutcomeSection cpo={c.cost_per_outcome} />
        </Section>
      )}

      {c.objections && c.objections.length > 0 && (
        <Section title="Common Objections">
          <div className="space-y-3">
            {c.objections.map((o, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm font-medium text-gray-800">{o.text}</p>
                  {o.count > 1 && (
                    <span className="shrink-0 text-xs text-gray-400">×{o.count}</span>
                  )}
                </div>
                {o.reframe && (
                  <p className="text-xs text-gray-500 mt-1.5">
                    <span className="font-medium">Suggested reframe:</span> {o.reframe}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {c.recommendation && (
        <Section title="Recommendation">
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4">
            <p className="text-sm text-gray-800 leading-relaxed">{c.recommendation}</p>
          </div>
        </Section>
      )}

      {c.next_steps && c.next_steps.length > 0 && (
        <Section title="Next Steps">
          <NextStepsList steps={c.next_steps} />
        </Section>
      )}
    </>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

function formatWeek(weekStart: string): string {
  const start = new Date(weekStart + 'T00:00:00');
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}, ${start.getFullYear()}`;
}

const TYPE_LABEL: Record<string, string> = {
  product_discovery: '🔬 Product Discovery',
  lead_generation: '🎯 Lead Generation',
};

const TYPE_COLOR: Record<string, string> = {
  product_discovery: 'bg-blue-50 text-blue-700 border-blue-200',
  lead_generation: 'bg-green-50 text-green-700 border-green-200',
};

const ReportDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<CustomerReport | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const handleDownloadPDF = async () => {
    if (!report) return;
    setDownloadingPDF(true);
    try {
      const [{ pdf }, { ReportPDF }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../components/reports/ReportPDF'),
      ]);
      const blob = await pdf(
        <ReportPDF report={report} groups={groups} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${report.week_start}-${report.report_type}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setDownloadingPDF(false);
    }
  };

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      // 1. Fetch the report (only published are visible to users via RLS + status check)
      const { data: rData } = await supabase
        .from('customer_reports')
        .select('id, report_type, week_start, published_at')
        .eq('id', id)
        .eq('status', 'published')
        .single();

      if (!rData) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setReport(rData);

      // 2. Fetch groups for this report
      const { data: gData } = await supabase
        .from('report_campaign_groups')
        .select('id, group_name, funnel, classified_breakdown, disposition_breakdown')
        .eq('report_id', id)
        .order('group_name');

      const rawGroups = gData ?? [];
      if (rawGroups.length === 0) {
        setGroups([]);
        setLoading(false);
        return;
      }

      // 3. Fetch content for all groups (flat query to avoid RLS nested join issues)
      const groupIds = rawGroups.map(g => g.id);
      const { data: cData } = await supabase
        .from('report_content')
        .select(
          'id, group_id, verdict, verdict_status, hypothesis_short, icp_brief, sequence_note, connecting_thread, sample_note, quotes, objections, hot_leads, win_loss_summary, message_performance, performance_tier, campaign_setup, split_test, title_breakdown, cost_per_outcome, recommendation, next_steps, date_range_start, date_range_end'
        )
        .in('group_id', groupIds);

      const contentByGroup = (cData ?? []).reduce<Record<string, ReportContent>>(
        (acc, c) => { acc[c.group_id] = c; return acc; },
        {}
      );

      setGroups(
        rawGroups.map(g => ({
          id: g.id,
          group_name: g.group_name,
          funnel: g.funnel ?? null,
          classified_breakdown: g.classified_breakdown ?? null,
          content: contentByGroup[g.id] ?? null,
        }))
      );
      setLoading(false);
    };

    load();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-center text-sm text-gray-500">
        Loading report...
      </div>
    );
  }

  if (notFound || !report) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-center">
        <p className="text-sm text-gray-500">Report not found or not yet published.</p>
        <button
          onClick={() => navigate('/reports')}
          className="mt-4 text-sm text-purple-600 hover:underline"
        >
          ← Back to Reports
        </button>
      </div>
    );
  }

  const isPD = report.report_type === 'product_discovery';

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Back */}
      <button
        onClick={() => navigate('/reports')}
        className="text-sm text-gray-500 hover:text-gray-700 mb-6 flex items-center gap-1"
      >
        ← All Reports
      </button>

      {/* Report header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {formatWeek(report.week_start)}
          </h1>
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${TYPE_COLOR[report.report_type]}`}
          >
            {TYPE_LABEL[report.report_type]}
          </span>
        </div>
        <div className="flex flex-col items-end gap-2">
          {report.published_at && (
            <p className="text-xs text-gray-400">
              Published{' '}
              {new Date(report.published_at).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          )}
          <button
            onClick={handleDownloadPDF}
            disabled={downloadingPDF}
            className="flex items-center gap-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg transition-colors"
          >
            {downloadingPDF ? (
              <>
                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Generating…
              </>
            ) : (
              <>
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download PDF
              </>
            )}
          </button>
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">
          No data available for this report.
        </p>
      ) : (
        <div className="space-y-10">
          {groups.map((group, idx) => (
            <div key={group.id}>
              {/* Group header — only show if multiple groups */}
              {groups.length > 1 && (
                <div className="flex items-center gap-3 mb-5">
                  <h2 className="text-base font-bold text-gray-800">{group.group_name}</h2>
                  {idx < groups.length - 1 && (
                    <div className="flex-1 border-t border-gray-200" />
                  )}
                </div>
              )}

              {/* Funnel */}
              {group.funnel && (
                <FunnelStats
                  funnel={group.funnel}
                  sampleNote={group.content?.sample_note ?? null}
                />
              )}

              {/* Content */}
              {group.content ? (
                <>
                  {isPD && group.content.verdict && (
                    <VerdictBox verdict={group.content.verdict} />
                  )}
                  {isPD ? (
                    <ProductDiscoveryContent c={group.content} />
                  ) : (
                    <LeadGenerationContent c={group.content} />
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-400 py-4">
                  Content is being processed for this group.
                </p>
              )}

              {idx < groups.length - 1 && (
                <div className="border-t border-gray-100 pt-2" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportDetail;
